import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Paper, Popper, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export const EXPORT_TRACK_TOOLTIP_MESSAGE =
  "Export requested. Go to Request Tracking to download when ready";

export const EXPORT_LIMIT_TOOLTIP_MESSAGE =
  "Maximum download limit is 10 lakh records. Apply filters accordingly.";

export const EXPORT_REDIRECT_BRAND_COLOR = "#6759FF";
const EXPORT_REDIRECT_ACTIVE_BG = "rgba(103, 89, 255, 0.08)";
const EXPORT_REDIRECT_ACTIVE_HOVER_BG = "rgba(103, 89, 255, 0.12)";

/** Shared MUI IconButton styles when export switches to Track mode. */
export function getExportRedirectIconButtonSx(isExportRedirectMode, overrides = {}) {
  return {
    border: "1px solid",
    borderColor: isExportRedirectMode ? EXPORT_REDIRECT_BRAND_COLOR : "grey.400",
    borderRadius: 1,
    color: isExportRedirectMode ? EXPORT_REDIRECT_BRAND_COLOR : "#333333",
    backgroundColor: isExportRedirectMode
      ? EXPORT_REDIRECT_ACTIVE_BG
      : "transparent",
    width: 36,
    height: 36,
    "&:hover": {
      borderColor: isExportRedirectMode ? EXPORT_REDIRECT_BRAND_COLOR : "grey.700",
      backgroundColor: isExportRedirectMode
        ? EXPORT_REDIRECT_ACTIVE_HOVER_BG
        : "grey.50",
    },
    ...overrides,
  };
}

/** Shared MUI outlined Button styles when export switches to Track mode. */
export function getExportRedirectOutlinedButtonSx(isExportRedirectMode, baseSx = {}) {
  if (!isExportRedirectMode) {
    return baseSx;
  }

  return {
    ...baseSx,
    border: `1px solid ${EXPORT_REDIRECT_BRAND_COLOR} !important`,
    color: EXPORT_REDIRECT_BRAND_COLOR,
    bgcolor: EXPORT_REDIRECT_ACTIVE_BG,
    "&:hover": {
      ...baseSx["&:hover"],
      border: `1px solid ${EXPORT_REDIRECT_BRAND_COLOR} !important`,
      bgcolor: EXPORT_REDIRECT_ACTIVE_HOVER_BG,
      boxShadow: "none",
    },
    "&.Mui-focusVisible": {
      ...baseSx["&.Mui-focusVisible"],
      border: `1px solid ${EXPORT_REDIRECT_BRAND_COLOR} !important`,
      boxShadow: "none",
    },
  };
}

const BRAND_PRIMARY = EXPORT_REDIRECT_BRAND_COLOR;
const BRAND_SOFT_BG = "linear-gradient(135deg, #F4F2FF 0%, #EDE9FF 100%)";

const hintPaperSx = {
  position: "relative",
  px: 1.75,
  py: 1.25,
  maxWidth: 300,
  borderRadius: "10px",
  background: BRAND_SOFT_BG,
  border: `1px solid ${BRAND_PRIMARY}`,
  boxShadow: `0 8px 24px rgba(103, 89, 255, 0.22)`,
  overflow: "visible",
};

const hintArrowTopSx = {
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    width: 12,
    height: 12,
    boxSizing: "border-box",
    transform: "translate(-50%, 50%) rotate(45deg)",
    background: BRAND_SOFT_BG,
    borderRight: `1px solid ${BRAND_PRIMARY}`,
    borderBottom: `1px solid ${BRAND_PRIMARY}`,
    zIndex: 0,
  },
};

const hintArrowBottomSx = {
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "50%",
    width: 12,
    height: 12,
    boxSizing: "border-box",
    transform: "translate(-50%, -50%) rotate(45deg)",
    background: BRAND_SOFT_BG,
    borderLeft: `1px solid ${BRAND_PRIMARY}`,
    borderTop: `1px solid ${BRAND_PRIMARY}`,
    zIndex: 0,
  },
};

/**
 * Hover tooltip + non-blocking hint near the download/Track control after export success.
 */
export default function ExportDownloadTooltip({
  title,
  children,
  autoOpen = false,
  autoOpenDurationMs = 8000,
  snackbarMessage = EXPORT_TRACK_TOOLTIP_MESSAGE,
  arrow = true,
  placement = "top",
  hintPlacement = "bottom",
  showCloseButton = false,
  onHintClose,
}) {
  const [hintOpen, setHintOpen] = useState(false);
  const anchorRef = useRef(null);
  const wasAutoOpenRef = useRef(false);

  useEffect(() => {
    // Show hint only on false -> true transition (after export success)
    if (autoOpen && !wasAutoOpenRef.current) {
      setHintOpen(true);
    }
    if (!autoOpen) {
      setHintOpen(false);
    }
    wasAutoOpenRef.current = autoOpen;
  }, [autoOpen]);

  useEffect(() => {
    if (!hintOpen) return undefined;
    const timerId = setTimeout(() => setHintOpen(false), autoOpenDurationMs);
    return () => clearTimeout(timerId);
  }, [hintOpen, autoOpenDurationMs]);

  const handleCloseHint = (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setHintOpen(false);
    onHintClose?.();
  };

  const hintOffset = [0, 8];
  const hintArrowSx = hintPlacement === "top" ? hintArrowTopSx : hintArrowBottomSx;

  return (
    <>
      <Tooltip title={title} arrow={arrow} placement={placement}>
        <Box
          ref={anchorRef}
          component="span"
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          {children}
        </Box>
      </Tooltip>
      <Popper
        open={hintOpen && Boolean(anchorRef.current)}
        anchorEl={anchorRef.current}
        placement={hintPlacement}
        modifiers={[{ name: "offset", options: { offset: hintOffset } }]}
        sx={{ zIndex: (theme) => (theme?.zIndex?.tooltip ?? 1500) + 1 }}
      >
        <Paper elevation={0} sx={{ ...hintPaperSx, ...hintArrowSx }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, position: "relative", zIndex: 1 }}>
            <InfoOutlinedIcon
              sx={{
                fontSize: 18,
                color: BRAND_PRIMARY,
                mt: "1px",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: BRAND_PRIMARY,
                lineHeight: 1.45,
                flex: 1,
                pr: showCloseButton ? 0.5 : 0,
              }}
            >
              {snackbarMessage}
            </Typography>
            {showCloseButton && (
              <IconButton
                size="small"
                aria-label="Close hint"
                onClick={handleCloseHint}
                sx={{
                  p: 0.25,
                  mt: "-2px",
                  mr: "-4px",
                  color: BRAND_PRIMARY,
                  flexShrink: 0,
                  "&:hover": { backgroundColor: "rgba(103, 89, 255, 0.12)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Paper>
      </Popper>
    </>
  );
}
